import { generateUuid } from "../../utils/id";
import type { JointUUID, Site, TendonSpatialRef, TendonUUID } from "../../types/scene";
import type { ParsedTendonJointRef, ParsedTendonsMap } from "./types";

type TendonParser = {
  xmlDoc: Document;
  classParentMap: Record<string, string | undefined>;
  tendonClassDefaults: Record<string, Record<string, string> | undefined>;
  tendonDefaultScope: Record<string, string>;
  sitesMap: Record<string, Site>;
  jointNameToUuid: Record<string, JointUUID | undefined>;
  parsePassthroughAttributes: (
    node: Element,
    exclude?: string[]
  ) => Record<string, string> | undefined;
  coerceUuidFromMjcfName: (name: string) => TendonUUID | null;
  warn: (message: string) => void;
};

export const resolveTendonAttributes = (
  parser: unknown,
  node: Element
): Record<string, string> | undefined => {
  const tendonParser = parser as TendonParser;
  const explicit = tendonParser.parsePassthroughAttributes(node, ["name"]) || {};
  const className = explicit.class;
  const chain: string[] = [];
  let currentClass: string | undefined = className;
  while (currentClass) {
    chain.unshift(currentClass);
    currentClass = tendonParser.classParentMap[currentClass] || undefined;
  }

  let resolved: Record<string, string> = { ...tendonParser.tendonDefaultScope };
  chain.forEach((klass) => {
    const defaults = tendonParser.tendonClassDefaults[klass];
    if (defaults) resolved = { ...resolved, ...defaults };
  });
  resolved = { ...resolved, ...explicit };
  return Object.keys(resolved).length > 0 ? resolved : undefined;
};

const resolveTendonSiteRef = (
  parser: unknown,
  siteName: string | null,
  tendonName: string
): string | null => {
  const tendonParser = parser as TendonParser;
  if (!siteName) return null;
  const site = Object.values(tendonParser.sitesMap || {}).find(
    (candidate) => candidate?.name === siteName
  );
  if (site?.uuid) return site.uuid;
  tendonParser.warn(
    `[MJCFParser] Spatial tendon "${tendonName}" references missing site "${siteName}"`
  );
  return siteName;
};

export const parseSpatialTendonRoute = (
  parser: unknown,
  node: Element,
  tendonName: string
): TendonSpatialRef[] => {
  const tendonParser = parser as TendonParser;
  const route: TendonSpatialRef[] = [];
  Array.from(node.children || []).forEach((child) => {
    const tag = child.tagName?.toLowerCase();
    if (tag === "site") {
      const site = resolveTendonSiteRef(parser, child.getAttribute("site"), tendonName);
      if (site) route.push({ kind: "site", site });
      return;
    }
    if (tag === "geom") {
      const geom = child.getAttribute("geom");
      if (!geom) return;
      const sidesite = resolveTendonSiteRef(parser, child.getAttribute("sidesite"), tendonName);
      route.push({
        kind: "geom",
        geom,
        ...(sidesite ? { sidesite } : {}),
      });
      return;
    }
    if (tag === "pulley") {
      const divisor = Number(child.getAttribute("divisor"));
      route.push({
        kind: "pulley",
        ...(Number.isFinite(divisor) ? { divisor } : {}),
      });
    }
  });
  if (route.length === 0) {
    tendonParser.warn(
      `[MJCFParser] Spatial tendon "${tendonName}" has no supported route elements`
    );
  }
  return route;
};

export const parseTendons = (parser: unknown): ParsedTendonsMap => {
  const tendonParser = parser as TendonParser;
  const tendons: ParsedTendonsMap = {};
  const tendonRoots = Array.from(
    tendonParser.xmlDoc.querySelectorAll("mujoco > tendon")
  ) as Element[];
  if (tendonRoots.length === 0) return tendons;

  let fixedGlobalIndex = 0;
  let spatialGlobalIndex = 0;

  for (const tendonRoot of tendonRoots) {
    let fixedNodes = Array.from(tendonRoot.querySelectorAll(":scope > fixed")) as Element[];
    if (fixedNodes.length === 0) {
      fixedNodes = Array.from(tendonRoot.children).filter(
        (child) => child.tagName?.toLowerCase() === "fixed"
      ) as Element[];
    }
    fixedNodes.forEach((node: Element) => {
      const index = fixedGlobalIndex++;
      const name = node.getAttribute("name") || `tendon_${index + 1}`;
      const stableUuid = tendonParser.coerceUuidFromMjcfName(name);
      const uuid = (stableUuid || generateUuid()) as TendonUUID;

      const joints: ParsedTendonJointRef[] = [];
      let jointNodes = Array.from(node.querySelectorAll(":scope > joint")) as Element[];
      if (jointNodes.length === 0) {
        jointNodes = Array.from(node.children).filter(
          (child) => child.tagName?.toLowerCase() === "joint"
        ) as Element[];
      }
      jointNodes.forEach((jointNode) => {
        const jointName = jointNode.getAttribute("joint");
        if (!jointName) return;
        const jointUuid = tendonParser.jointNameToUuid[jointName];
        if (!jointUuid) {
          tendonParser.warn(
            `[MJCFParser] Tendon "${name}" references missing joint "${jointName}"`
          );
          return;
        }
        const coefRaw = Number(jointNode.getAttribute("coef"));
        joints.push({
          joint: jointUuid,
          coef: Number.isFinite(coefRaw) ? coefRaw : 1,
        });
      });

      tendons[uuid] = {
        uuid,
        name,
        kind: "fixed",
        joints,
        attributes: resolveTendonAttributes(parser, node),
      };
    });

    let spatialNodes = Array.from(tendonRoot.querySelectorAll(":scope > spatial")) as Element[];
    if (spatialNodes.length === 0) {
      spatialNodes = Array.from(tendonRoot.children).filter(
        (child) => child.tagName?.toLowerCase() === "spatial"
      ) as Element[];
    }
    spatialNodes.forEach((node: Element) => {
      const index = spatialGlobalIndex++;
      const name = node.getAttribute("name") || `spatial_tendon_${index + 1}`;
      const stableUuid = tendonParser.coerceUuidFromMjcfName(name);
      const uuid = (stableUuid || generateUuid()) as TendonUUID;
      tendons[uuid] = {
        uuid,
        name,
        kind: "spatial",
        joints: [],
        spatial: parseSpatialTendonRoute(parser, node, name),
        attributes: resolveTendonAttributes(parser, node),
      };
    });
  }

  return tendons;
};
